import React, { ChangeEvent, FC, FocusEvent, ReactNode, Ref, useState } from 'react';

import { CommonComponentProps, CommonFieldProps, StylableComponentProps, InputProps } from '@cld/common-definitions';
import { DecorationWrapper } from './components/DecorationWrapper';
import { FieldBase } from './components/FieldBase';
import { FieldSize } from './FieldSize';

import './TextField.theme';

export interface TextFieldProps extends CommonFieldProps<string, ChangeEvent<HTMLInputElement>>, StylableComponentProps, CommonComponentProps<'div'> {
  /** Type of the HTML input that will be used inside. */
  type: 'email' | 'password' | 'search' | 'tel' | 'text' | 'url' | 'number';
  /** Id of the HTML input. */
  id?: string;
  /** Displayed inside the field when the value is empty. */
  placeholder?: string;
  /** Desired size of the field. */
  size?: FieldSize;
  /** Decoration **before** the actual HTML input inside the field. */
  leadingDecoration?: ReactNode;
  /** Decoration **after** the actual HTML input inside the field. */
  trailingDecoration?: ReactNode;
  /** Props passed directly to HTML input inside **(overriding props already handled by abstraction)**. */
  inputProps?: InputProps;
  /** Ref forwarded to HTML input element inside. */
  inputRef?: Ref<HTMLInputElement>;
}

/** Controlled text field component - does not handle any other responsibilities beside displaying and
 * controlling the field itself. */
const TextField: FC<TextFieldProps> = ({
  className,
  defaultValue,
  id,
  isDisabled = false,
  isReadonly = false,
  inputProps,
  inputRef,
  leadingDecoration,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  rootProps,
  size = 'xs',
  trailingDecoration,
  type,
  validationState = 'none',
  value,
  name,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event.target.value, event);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  return (
    <FieldBase
      className={className}
      isDisabled={isDisabled}
      hasLeadingDecoration={!!leadingDecoration}
      hasTrailingDecoration={!!trailingDecoration}
      isFocused={isFocused}
      size={size}
      validationState={validationState}
      {...rootProps}
    >
      {leadingDecoration && <DecorationWrapper size={size}>{leadingDecoration}</DecorationWrapper>}

      <input
        defaultValue={defaultValue}
        disabled={isDisabled}
        id={id}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        readOnly={isReadonly}
        ref={inputRef}
        type={type}
        value={value}
        {...inputProps}
      />

      {trailingDecoration && <DecorationWrapper size={size}>{trailingDecoration}</DecorationWrapper>}
    </FieldBase>
  );
};

export default TextField;
